import React, { useEffect, useMemo, useState } from 'react';
import { useCreateBlogMutation, useUpdateBlogMutation } from '../blogsApiSlice';

function slugify(input = '') {
  return String(input)
    .toLowerCase()
    .trim()
    .replace(/['"]/g, '')
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-+|-+$/g, '');
}

export default function EditBlogForm({ id, initial = {}, onSaved, onCancel }) {
  const [form, setForm] = useState({
    title: '',
    slug: '',
    summary: '',
    content: '',
    coverImageUrl: '',
    tags: '',
    isPublic: true,
    isPublished: false,
    ...initial,
  });

  // mutations inside the form component (update/create)
  const [createBlog, { isLoading: creating }] = useCreateBlogMutation();
  const [updateBlog, { isLoading: updating }] = useUpdateBlogMutation();
  const submitting = creating || updating;

  // convert array -> CSV for UI if needed when initial changes
  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      ...initial,
      tags: Array.isArray(initial.tags)
        ? initial.tags.join(', ')
        : typeof initial.tags === 'string'
        ? initial.tags
        : prev.tags ?? '',
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial && JSON.stringify(initial)]); // stringify to detect deep changes

  const [errors, setErrors] = useState({});
  const [slugTouched, setSlugTouched] = useState(false);
  const [dirty, setDirty] = useState(false);

  // track dirty
  useEffect(() => {
    setDirty(true);
  }, [form.title, form.slug, form.summary, form.content, form.coverImageUrl, form.tags, form.isPublic, form.isPublished]);

  // auto-slug from title (until slug is manually touched)
  useEffect(() => {
    if (!slugTouched) {
      setForm((f) => ({ ...f, slug: slugify(f.title || '') }));
    }
  }, [form.title, slugTouched]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'slug') setSlugTouched(true);
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const validate = useMemo(() => {
    const v = {};
    if (!form.title?.trim()) v.title = 'Title is required';
    if (!form.slug?.trim()) v.slug = 'Slug is required';
    return v;
  }, [form.title, form.slug]);

  const markClean = () => setDirty(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const v = validate;
    setErrors(v);
    if (Object.keys(v).length) return;

    const payload = {
      ...form,
      tags: String(form.tags || '')
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
    };

    try {
      if (id) {
        // update
        const res = await updateBlog({ id, ...payload }).unwrap();
        markClean();
        onSaved?.({ id: res._id || id, action: 'updated' });
      } else {
        // create
        const res = await createBlog(payload).unwrap();
        markClean();
        onSaved?.({ id: res._id || res.id, action: 'created' });
      }
    } catch (err) {
      console.error('Save error', err);
      const message = err?.data?.message ?? err?.message ?? 'Failed to save';
      // set a top-level error
      setErrors((prev) => ({ ...prev, _form: message }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {errors._form && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-red-700">{errors._form}</div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700">Title</label>
        <input
          name="title"
          value={form.title}
          onChange={handleChange}
          className={`mt-1 w-full rounded-xl border px-3 py-2 outline-none focus:ring ${errors.title ? 'border-red-500' : ''}`}
          required
        />
        {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-gray-700">Slug</label>
          <input
            name="slug"
            value={form.slug}
            onChange={handleChange}
            onBlur={() => setForm((f) => ({ ...f, slug: slugify(f.slug) }))}
            className={`mt-1 w-full rounded-xl border px-3 py-2 outline-none focus:ring ${errors.slug ? 'border-red-500' : ''}`}
            required
          />
          {errors.slug && <p className="mt-1 text-sm text-red-600">{errors.slug}</p>}
          <p className="mt-1 text-xs text-gray-500">URL: /blogs/{form.slug || 'your-slug'}</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Cover Image URL</label>
          <input
            name="coverImageUrl"
            value={form.coverImageUrl}
            onChange={handleChange}
            className="mt-1 w-full rounded-xl border px-3 py-2 outline-none focus:ring"
            placeholder="https://…"
          />
        </div>
      </div>

      {form.coverImageUrl ? (
        <div className="rounded-2xl border border-gray-200 bg-white p-3">
          <img src={form.coverImageUrl} alt="Cover preview" className="max-h-48 w-full object-cover rounded-xl" />
        </div>
      ) : null}

      <div>
        <label className="block text-sm font-medium text-gray-700">Summary</label>
        <textarea
          name="summary"
          value={form.summary}
          onChange={handleChange}
          rows={3}
          className="mt-1 w-full rounded-xl border px-3 py-2 outline-none focus:ring"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Content</label>
        <textarea
          name="content"
          value={form.content}
          onChange={handleChange}
          rows={10}
          className="mt-1 w-full rounded-xl border px-3 py-2 outline-none focus:ring"
        />
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-gray-700">Tags (comma separated)</label>
          <input
            name="tags"
            value={form.tags}
            onChange={handleChange}
            className="mt-1 w-full rounded-xl border px-3 py-2 outline-none focus:ring"
            placeholder="iot, ai, update"
          />
        </div>

        <div className="flex items-end gap-6">
          <label className="flex items-center gap-2">
            <input type="checkbox" name="isPublic" checked={!!form.isPublic} onChange={handleChange} />
            <span className="text-sm text-gray-700">Public</span>
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" name="isPublished" checked={!!form.isPublished} onChange={handleChange} />
            <span className="text-sm text-gray-700">Published</span>
          </label>
        </div>
      </div>

      <div className="flex items-center gap-3 pt-2">
        <button
          type="submit"
          className="rounded-xl bg-black px-5 py-2 text-white hover:opacity-90 disabled:opacity-50"
          disabled={submitting}
        >
          {submitting ? 'Saving…' : 'Save'}
        </button>

        <button
          type="button"
          onClick={() => onCancel?.({ dirty })}
          className="rounded-xl border px-5 py-2 text-gray-800 hover:bg-gray-50"
          disabled={submitting}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
